/**
 * Created by camiel on 21/04/2017.
 */

import Bitmap = createjs.Bitmap;
import Container = createjs.Container;
import LoadQueue = createjs.LoadQueue;
import Tween = createjs.Tween;

import AssetManager from "lib/src/AssetManager";
import Utils from "lib/src/utils";

import { InstrumentId } from "src/KeyboardSounds";
import SoundManager from "lib/src/SoundManager";
import Analytics from "lib/src/Analytics";

const DRUMS_BUTTON_ID = "drums-button";
const DRUMS_BUTTON_PRESSED_BACKGROUND_ID = "drums-button-pressed-background";
const DRUM_BUTTON_SOUND = {src: 'sound/drum_spelen.mp3', id: 'sound_drum'};
const GUITAR_BUTTON_ID = "guitar-button";
const GUITAR_BUTTON_PRESSED_BACKGROUND_ID = "guitar-button-pressed-background";
const GUITAR_BUTTON_SOUND = {src: 'sound/gitaar_spelen.mp3', id: 'sound_guitar'};
const PIANO_BUTTON_ID = "piano-button";
const PIANO_BUTTON_PRESSED_BACKGROUND_ID = "piano-button-pressed-background";
const PIANO_BUTTON_SOUND = {src: 'sound/piano_spelen.mp3', id: 'sound_piano'};
const TRUMPET_BUTTON_ID = "trumpet-button";
const TRUMPET_BUTTON_PRESSED_BACKGROUND_ID = "trumpet-button-pressed-background";
const TRUMPET_BUTTON_SOUND = {src: 'sound/trompet_spelen.mp3', id: 'sound_trumpet'};

const CIRCLE_WHITE_ID = "circle-white";
const BUTTON_PRESSED_GLOW_ID = "button-pressed-glow";

export default class InstrumentButton {
  public static addToManifest(manifest: any[]): void {
    const assets = [
      DRUMS_BUTTON_ID,
      DRUMS_BUTTON_PRESSED_BACKGROUND_ID,
      GUITAR_BUTTON_ID,
      GUITAR_BUTTON_PRESSED_BACKGROUND_ID,
      PIANO_BUTTON_ID,
      PIANO_BUTTON_PRESSED_BACKGROUND_ID,
      TRUMPET_BUTTON_ID,
      TRUMPET_BUTTON_PRESSED_BACKGROUND_ID,
      CIRCLE_WHITE_ID,
      BUTTON_PRESSED_GLOW_ID,
    ];

    assets
      .map((id) => ({
        id,
        src: AssetManager.getShared(`${id}.png`),
      }))
      .forEach((asset) => manifest.push(asset));

    [DRUM_BUTTON_SOUND, GUITAR_BUTTON_SOUND, PIANO_BUTTON_SOUND, TRUMPET_BUTTON_SOUND].forEach((o) => {
      manifest.push({src: AssetManager.getShared(o.src), id: o.id});
    });
  }

  public readonly container: Container;

  private _buttonContainer: Container;
  private _buttonPressedBackground: Bitmap;
  private _buttonPressedGlow: Bitmap;
  private _circleRed: Bitmap;

  constructor(
    public readonly instrumentId: InstrumentId,
    private readonly _preLoadQueue: LoadQueue,
    private readonly _onClick: (instrumentId: InstrumentId) => void,
  ) {
    this.container = new Container();

    this._initializeButton();
    this._initializeCircle();

    this._buttonPressedBackground.alpha = 0;
    this._buttonPressedGlow.alpha = 0;
    this._circleRed.alpha = 0;
  }

  public setActive(active: boolean, animated: boolean): void {
    const pressedAlpha = active ? 1 : 0;

    this._buttonPressedBackground.alpha = pressedAlpha;

    const pressedObjects = [
      this._buttonPressedGlow,
      this._circleRed,
    ];

    for (const pressedObject of pressedObjects) {

      if (animated) {
        Tween
          .get(pressedObject)
          .to({ alpha: pressedAlpha }, 300);
      } else {
        pressedObject.alpha = pressedAlpha;
      }
    }

    if (active && animated) {
      const scaleDown = 0.95;
      Tween
        .get(this._buttonContainer)
        .to({scaleX: scaleDown, scaleY: scaleDown}, 75)
        .to({scaleX: 1, scaleY: 1}, 75);
    }
  }

  private _initializeButton(): void {
    this._buttonContainer = new Container();

    const buttonImage = this._preLoadQueue.getResult(this._buttonImageId) as HTMLImageElement;
    const button = new Bitmap(buttonImage);

    button.on("click", () => {
      this._playSound();
      Analytics.logEvent("keyboard-select-instrument", {instrument: this.instrumentId, _label: this.instrumentId});
      this._onClick(this.instrumentId);
    });

    Utils.centerBitmap(button);

    const buttonPressedBackgroundImage =
      this._preLoadQueue.getResult(this._buttonPressedBackgroundImageId) as HTMLImageElement;
    this._buttonPressedBackground = new Bitmap(buttonPressedBackgroundImage);

    Utils.centerBitmap(this._buttonPressedBackground);

    const buttonPressedGlowImage =
      this._preLoadQueue.getResult(BUTTON_PRESSED_GLOW_ID) as HTMLImageElement;
    this._buttonPressedGlow = new Bitmap(buttonPressedGlowImage);

    Utils.centerBitmap(this._buttonPressedGlow);

    this._buttonContainer.addChild(this._buttonPressedGlow);
    this._buttonContainer.addChild(this._buttonPressedBackground);
    this._buttonContainer.addChild(button);

    this.container.addChild(this._buttonContainer);
  }

  private _initializeCircle(): void {
    this._createCircle(255, 255, 255);

    this._circleRed = this._createCircle(255, 0, 0);
  }

  private _createCircle(redOffset: number, greenOffset: number, blueOffset: number): Bitmap {
    const circleWhiteImage = this._preLoadQueue.getResult(CIRCLE_WHITE_ID) as HTMLImageElement;
    const circle = new Bitmap(circleWhiteImage);

    Utils.centerBitmap(circle);
    circle.y = -this._buttonPressedBackground.image.height / 2 - 30;
    this.container.addChild(circle);

    circle.filters = [
      new createjs.ColorFilter(0, 0, 0, 1, redOffset, greenOffset, blueOffset, 0),
    ];

    circle.cache(0, 0, circleWhiteImage.width, circleWhiteImage.height);

    return circle;
  }

  private get _buttonImageId(): string {
    switch (this.instrumentId) {
      case "drums":
        return DRUMS_BUTTON_ID;
      case "guitar":
        return GUITAR_BUTTON_ID;
      case "piano":
        return PIANO_BUTTON_ID;
      case "trumpet":
        return TRUMPET_BUTTON_ID;

      default:
        return null;
    }
  }

  private get _buttonPressedBackgroundImageId(): string {
    switch (this.instrumentId) {
      case "drums":
        return DRUMS_BUTTON_PRESSED_BACKGROUND_ID;
      case "guitar":
        return GUITAR_BUTTON_PRESSED_BACKGROUND_ID;
      case "piano":
        return PIANO_BUTTON_PRESSED_BACKGROUND_ID;
      case "trumpet":
        return TRUMPET_BUTTON_PRESSED_BACKGROUND_ID;

      default:
        return null;
    }
  }

  private _playSound() {
    switch (this.instrumentId) {
      case "drums":
        SoundManager.playSound(DRUM_BUTTON_SOUND.id);
        break;
      case "guitar":
        SoundManager.playSound(GUITAR_BUTTON_SOUND.id);
        break;
      case "piano":
        SoundManager.playSound(PIANO_BUTTON_SOUND.id);
        break;
      case "trumpet":
        SoundManager.playSound(TRUMPET_BUTTON_SOUND.id);
        break;
    }
  }
}



// WEBPACK FOOTER //
// ./src/InstrumentButton.ts